import React, { useMemo } from 'react';
import { parseISO } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NotAvailableYet } from '@/components/NotAvailableYet';
import { notAvailableYetHint } from '@/lib/notAvailableYet';
import { useInventoryLedgerSources } from '@/hooks/useInventoryLedgerSources';
import { formatInventoryLedgerSourceForDisplay } from '@/shared/inventoryLedgerSourceDisplay';
import { ConnectionsMap, CsvFile, formatFeedDate, groupMerchantFeeds, latestDate } from '@/shared/merchantFeeds';
import { featureFlags } from '@/utils/featureFlags';
import { cn } from '@/lib/utils';
import { SettingsSection } from './SettingsGroup';

// "<Merchant> Inventory Value Chain" (FIG-322 / REQ-036, REQ-043–049): a
// vertical sum of Rebates + On order + Inventory held + Sold (payables) =
// Total Expected Value. Only what exists today is shown: the inventory data
// sources (API / CSV / manual) and CSV staleness. Amounts per stage, row
// exclusions, locations, days of sales and the on-order rule need UC-222;
// Rebates is a placeholder by decision (16 Oct 2026).

interface InventoryValueChainSectionProps {
  merchantId: string;
  merchantName: string;
  isReseller: boolean;
  canEdit: boolean;
  /** The merchant's connections; CSV upload times come from here (the registry has none for CSVs). */
  connections?: ConnectionsMap | null;
  /** Opens the Inventory tab, where CSV snapshots are uploaded. */
  onOpenInventory?: () => void;
}

const VALUE_CHAIN_USE_CASE = 'UC-222';
const CSV_STALE_DAYS = 7;

const ORIGIN_LABELS: Record<string, string> = { live: 'API', csv_import: 'CSV', manual: 'Manual' };

// Inventory CSV uploads never set the registry's last_sync_at, so a CSV
// source's last update is its latest upload in the merchant's connections
// (matched by platform marker, e.g. _inventory_amazon_).
const platformOfSource = (source: string): string | null => {
  const s = source.toLowerCase();
  if (s.startsWith('amazon')) return 'amazon';
  if (s.startsWith('shopify')) return 'shopify';
  return null;
};

function latestCsvUploadFor(source: string, files: CsvFile[]): string | undefined {
  const platform = platformOfSource(source);
  const matching = files.filter((file) => {
    if (!platform) return true;
    const marker = (file.importId ?? file.connectionKey).match(/_inventory_(amazon|shopify)_/i)?.[1]?.toLowerCase();
    return marker === platform;
  });
  return latestDate(matching.map((file) => file.uploadedAt));
}

// Whole days since the upload, independent of the viewer's timezone.
function csvAgeInDays(lastUpdated: string | null | undefined, now = new Date()): number | null {
  if (!lastUpdated) return null;
  const time = parseISO(lastUpdated).getTime();
  if (Number.isNaN(time)) return null;
  return Math.floor((now.getTime() - time) / 86_400_000);
}

const Stage: React.FC<{
  title: string;
  subtitle: string;
  greyed?: boolean;
  operator?: '+' | '=';
  children?: React.ReactNode;
}> = ({ title, subtitle, greyed, operator, children }) => (
  <li className="flex gap-3">
    <div aria-hidden="true" className="w-4 pt-3 text-center font-semibold text-muted-foreground">{operator}</div>
    <div className={cn('flex-1 rounded-md border p-4', greyed && 'bg-muted/40 text-muted-foreground')}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-semibold">{title}</div>
        <div className="text-sm">{subtitle}</div>
      </div>
      {children && <div className="mt-3">{children}</div>}
    </div>
  </li>
);

export const InventoryValueChainSection: React.FC<InventoryValueChainSectionProps> = ({
  merchantId,
  merchantName,
  isReseller,
  canEdit,
  connections,
  onOpenInventory,
}) => {
  const { data: sources, isLoading, isError } = useInventoryLedgerSources(merchantId);
  const csvFiles = useMemo(() => groupMerchantFeeds(connections).inventoryCsvFiles, [connections]);

  return (
    <SettingsSection
      title={`${merchantName} Inventory Value Chain`}
      description="Controls which data feeds every EVE calculation for this merchant. Excluded boxes stay visible but are left out of all calculations."
    >
      <ol aria-label="Value chain stages" className="space-y-3">
        <Stage title="Rebates" subtitle="Placeholder · not tracked yet" greyed>
          <p className="text-sm">
            Marketplace rebates earned during Prime Day or other sales events. EVE does not handle them yet, so they are not counted.
          </p>
        </Stage>

        <Stage
          title="On order"
          operator="+"
          subtitle={isReseller ? '' : 'Resellers only'}
          greyed={!isReseller}
        >
          {isReseller && <NotAvailableYet useCase={VALUE_CHAIN_USE_CASE} />}
        </Stage>

        <Stage title="Inventory held" operator="+" subtitle="">
          <div data-testid="value-chain-inventory-held">
            {isLoading && (
              <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading data sources…
              </span>
            )}
            {isError && <p role="alert" className="text-sm text-destructive">Inventory data sources could not be loaded.</p>}
            {sources && sources.length === 0 && <p className="text-sm text-muted-foreground">No inventory data sources yet.</p>}
            {sources && sources.length > 0 && (
              <ul className="space-y-2" aria-label="Inventory held by data source">
                {sources.map((source) => {
                  const isCsv = source.origin_kind === 'csv_import';
                  const lastUpdated = isCsv ? latestCsvUploadFor(source.source, csvFiles) ?? source.last_sync_at : source.last_sync_at;
                  const age = isCsv ? csvAgeInDays(lastUpdated) : null;
                  const stale = age !== null && age > CSV_STALE_DAYS;
                  return (
                    <li key={source.source} className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm">
                      <span className="font-medium">{source.display_label || formatInventoryLedgerSourceForDisplay(source.source)}</span>
                      <span className="rounded-full border px-2 text-xs">{ORIGIN_LABELS[source.origin_kind] ?? source.origin_kind}</span>
                      {lastUpdated && (
                        <span className="text-xs text-muted-foreground">Last updated {formatFeedDate(lastUpdated)}</span>
                      )}
                      {stale && (
                        <span className="rounded-full border border-amber-300 bg-amber-50 px-2 text-xs font-semibold text-amber-900">
                          {age} days old
                        </span>
                      )}
                      <span className="ml-auto text-xs text-muted-foreground">
                        Expected Value: <NotAvailableYet useCase={VALUE_CHAIN_USE_CASE} className="text-xs" />
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {canEdit && (
                <Button type="button" variant="outline" size="sm" disabled
                  aria-label={`Include or exclude rows. ${notAvailableYetHint(VALUE_CHAIN_USE_CASE)}`}
                  title={notAvailableYetHint(VALUE_CHAIN_USE_CASE)}>
                  Include or exclude rows
                </Button>
              )}
              {/* The Inventory tab only offers uploads with inventory adjust enabled. */}
              {onOpenInventory && featureFlags.showInventoryAdjust && (
                <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={onOpenInventory}>
                  Upload a CSV on the Inventory tab
                </Button>
              )}
            </div>
          </div>
        </Stage>

        <Stage title="Sold (payables)" operator="+" subtitle="">
          <p className="text-sm text-muted-foreground">
            Sales net of platform fees over each platform's days of sales (defaults Amazon 7, Shopify 3), due to the RCO account.
          </p>
          <div className="mt-1"><NotAvailableYet useCase={VALUE_CHAIN_USE_CASE} /></div>
        </Stage>

        <Stage title="Total Expected Value" operator="=" subtitle="">
          <NotAvailableYet useCase={VALUE_CHAIN_USE_CASE} />
        </Stage>
      </ol>
    </SettingsSection>
  );
};

export default InventoryValueChainSection;
